import * as React from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Typography } from "@mui/material";
import Stack from "@mui/material/Stack";
import { useTranslation } from "react-i18next";
import TranslateIcon from "@mui/icons-material/Translate";
import { LltsLanguage, UtilsService } from "gen/clients/llts";
import { S3FileUploader } from "utils/S3FileUploader";
import { createFileKey } from "utils/s3Utils";
import Alert from "@mui/material/Alert";

interface Props {
  files: File[];
  onLanguageDetected: (language: LltsLanguage) => void;
  onClose: () => void;
}

const FILE_TYPE_PRIORITY = ["pdf", "docx", "txt", "pptx", "xlsx"];

const getFileExtension = (fileName: string): string => {
  const lastDot = fileName.lastIndexOf(".");
  return lastDot >= 0 ? fileName.substring(lastDot + 1).toLowerCase() : "";
};

const selectFileByPriority = (files: File[]): File => {
  for (const ext of FILE_TYPE_PRIORITY) {
    const match = files.find(f => getFileExtension(f.name) === ext);
    if (match) {
      return match;
    }
  }
  return files[0];
};

const I18N_PREFIX = "createProject.createProjectForm.detectLanguage";

const DetectLanguageDialog: React.FC<Props> = ({ files, onLanguageDetected, onClose }) => {
  const { t } = useTranslation();
  const [isDetecting, setIsDetecting] = React.useState(true);
  const [error, setError] = React.useState<unknown>();

  React.useEffect(() => {
    let cancelled = false;

    const detect = async () => {
      const file = selectFileByPriority(files);
      try {
        const presignedPost = await UtilsService.createTempFileUploadPresignedPost();
        await S3FileUploader.upload([file], presignedPost);
        const s3Key = createFileKey(presignedPost, file.name);
        const response = await UtilsService.detectLanguage({
          requestBody: { s3Key, fileName: file.name }
        });
        if (cancelled) {
          return;
        }
        onLanguageDetected(response.language);
        onClose();
      } catch (e) {
        if (cancelled) {
          return;
        }
        setError(e);
        setIsDetecting(false);
      }
    };

    detect();
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog open={true} onClose={onClose} fullWidth={true} maxWidth="xs">
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <TranslateIcon color="primary" />
          <span>{t(`${I18N_PREFIX}.dialogTitle`)}</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {isDetecting && (
          <Stack spacing={2} sx={{ py: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t(`${I18N_PREFIX}.detecting`)}
            </Typography>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary" noWrap={true}>
              {t(`${I18N_PREFIX}.analyzingFile`)}
            </Typography>
          </Stack>
        )}
        {error && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="error">{t(`${I18N_PREFIX}.errorMessage`)}</Alert>
            {/*<ApiErrorMessage apiError={error} />*/}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t(isDetecting ? "common.cancel" : "common.close")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export { DetectLanguageDialog };
